/* eslint-disable block-spacing */
/* eslint-disable one-var-declaration-per-line */
/* eslint-disable one-var */
/* eslint-disable no-plusplus */
import pLimit from 'p-limit';
import { Action, Dispatch } from 'redux';
import { PublishRenditionType } from '../api/vault/publishAssets';
import { IAppState } from './reducers';
import { getAssetFullRendition, getVideoSourceFromPreview } from '../components/AssetDetails/api/assetsLookupsMetatags';
import { getSourceLink } from '../api/vault/assetDetails';

export enum ActionType {
  StartLoading = 'SelectedAssetRenditions.StartLoading',
  SetRenditions = 'SelectedAssetRenditions.SetRenditions',
  SetRendition = 'SelectedAssetRenditions.SetRendition',
  ClearRenditions = 'SelectedAssetRenditions.ClearRenditions',
}

export interface IRendition {
  type: PublishRenditionType;
  fileFormat: string;
  label: string;
}

export interface IState {
  renditions: Map<string, IRendition[]>;
  isLoading: boolean;
}

export interface ISelectedAssetRenditionsAction extends Action<ActionType> {
  payload?: any;
}

const INITIAL_STATE: IState = {
  renditions: new Map(),
  isLoading: false,
};

export default (state: IState = INITIAL_STATE, action: ISelectedAssetRenditionsAction) => {
  switch (action.type) {
    case ActionType.StartLoading:
      return {
        ...state,
        isLoading: true,
      };
    case ActionType.SetRenditions:
      return {
        ...state,
        renditions: action.payload.renditions,
        isLoading: false,
      };
    case ActionType.SetRendition:
      return {
        ...state,
        renditions: new Map(state.renditions).set(action.payload.assetId, action.payload.rendition),
      };
    case ActionType.ClearRenditions:
      return {
        ...state,
        renditions: new Map(),
      };
    default:
      return state;
  }
};

export const getRenditions = (assetIds: string[]) => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  dispatch({
    type: ActionType.StartLoading,
  });
  const limit = pLimit(3);
  const promises: Promise<void>[] = [];
  const renditions = new Map<string, IRendition[]>();
  for (let i = 0; i < assetIds.length; i++) {
    const assetId = assetIds[i];
    promises.push(limit(async () => {
      let preview, full, source;
      try {
        preview = await getVideoSourceFromPreview(assetId);
      } catch (error) {/** Ignore */ }
      // VV-539: 'probe' — these three calls only establish which renditions exist so the
      // picker can offer them, and read the file extension off each signed URL for the
      // format label. A real URL is issued, so the access is logged, but recording it as
      // a Download would count a dropdown being opened as N downloads. The source call
      // can now also be refused outright (it requires downloadSource), which the existing
      // per-call catch already handles by omitting Source from the picker.
      try {
        full = await getAssetFullRendition(assetId, 'probe');
      } catch (error) {/** Ignore */ }
      try {
        const response = await getSourceLink({ id: assetId }, 1, 'probe');
        source = response?.data?.data;
      } catch (error) {/** Ignore */ }
      const foundRenditions: IRendition[] = [];
      if (preview) {
        foundRenditions.push({
          type: PublishRenditionType.Preview,
          fileFormat: getFileFormatFromUrl(preview),
          label: 'Preview',
        });
      }
      if (full) {
        foundRenditions.push({
          type: PublishRenditionType.Full,
          fileFormat: getFileFormatFromUrl(full),
          label: 'Master',
        });
      }
      if (source) {
        foundRenditions.push({
          type: PublishRenditionType.Source,
          fileFormat: getFileFormatFromUrl(source),
          label: 'Source',
        });
      }

      renditions.set(assetId, foundRenditions);
    }));
  }

  await Promise.all(promises);
  dispatch({
    type: ActionType.SetRenditions,
    payload: { renditions },
  });
};

export const setRendition = (assetId: string, rendition: string) => ({
  type: ActionType.SetRendition,
  payload: { assetId, rendition },
});

export const clearRenditions = () => ({
  type: ActionType.ClearRenditions,
});

const getFileFormatFromUrl = (urlString: string): string | null => urlString.split(/[#?]/)[0].split('.').pop().trim().toUpperCase();
